import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Search,
  ChevronLeft,
  Clock,
  Plus,
  Minus,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Link } from "wouter";

interface BalanceData {
  balance: {
    id: number;
    employeeId: number;
    policyId: number;
    currentBalance: string;
    usedBalance: string;
    pendingBalance: string;
    carryoverBalance?: string;
    year: number;
  };
  policy: {
    id: number;
    name: string;
    accrualUnit: string;
    color?: string;
  } | null;
  employee: {
    id: number;
    firstName: string;
    lastName: string;
    email?: string;
  } | null;
}

interface TimeOffPolicy {
  id: number;
  name: string;
  accrualUnit: string;
  color?: string;
}

export default function TimeOffBalances() {
  const [searchTerm, setSearchTerm] = useState("");
  const [policyFilter, setPolicyFilter] = useState<string>("all");
  const [isAdjustDialogOpen, setIsAdjustDialogOpen] = useState(false);
  const [selectedBalance, setSelectedBalance] = useState<BalanceData | null>(null);
  const [adjustmentAmount, setAdjustmentAmount] = useState("");
  const [adjustmentReason, setAdjustmentReason] = useState("");
  const currentYear = new Date().getFullYear();

  const { data: balances, isLoading } = useQuery<BalanceData[]>({
    queryKey: ["/api/time-off/balances", { year: currentYear.toString() }],
  });

  const { data: policies } = useQuery<TimeOffPolicy[]>({
    queryKey: ["/api/time-off/policies"],
  });

  const adjustMutation = useMutation({
    mutationFn: async (data: { employeeId: number; policyId: number; adjustment: number; reason: string }) => {
      return apiRequest("POST", "/api/time-off/balances/adjust", {
        ...data,
        year: currentYear,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/time-off/balances"] });
      toast({ title: "Balance adjusted", description: "The time off balance has been updated." });
      handleCloseAdjust();
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to adjust balance.", variant: "destructive" });
    },
  });

  const filteredBalances = useMemo(() => {
    if (!balances) return [];

    return balances.filter((b) => {
      const matchesSearch =
        !searchTerm ||
        `${b.employee?.firstName} ${b.employee?.lastName}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        b.policy?.name.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesPolicy = policyFilter === "all" || b.balance.policyId.toString() === policyFilter;

      return matchesSearch && matchesPolicy;
    });
  }, [balances, searchTerm, policyFilter]);

  const groupedByEmployee = useMemo(() => {
    const grouped: Record<number, BalanceData[]> = {};
    filteredBalances.forEach((b) => {
      if (!b.employee) return;
      if (!grouped[b.employee.id]) {
        grouped[b.employee.id] = [];
      }
      grouped[b.employee.id].push(b);
    });
    return grouped;
  }, [filteredBalances]);

  const handleOpenAdjust = (balance: BalanceData) => {
    setSelectedBalance(balance);
    setAdjustmentAmount("");
    setAdjustmentReason("");
    setIsAdjustDialogOpen(true);
  };

  const handleCloseAdjust = () => {
    setIsAdjustDialogOpen(false);
    setSelectedBalance(null);
    setAdjustmentAmount("");
    setAdjustmentReason("");
  };

  const handleAdjust = () => {
    if (!selectedBalance || !adjustmentAmount) return;
    const amount = parseFloat(adjustmentAmount);
    if (isNaN(amount) || amount === 0) {
      toast({ title: "Invalid amount", description: "Please enter a valid adjustment amount.", variant: "destructive" });
      return;
    }
    if (!adjustmentReason.trim()) {
      toast({ title: "Reason required", description: "Please provide a reason for the adjustment.", variant: "destructive" });
      return;
    }

    adjustMutation.mutate({
      employeeId: selectedBalance.balance.employeeId,
      policyId: selectedBalance.balance.policyId,
      adjustment: amount,
      reason: adjustmentReason,
    });
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Link href="/time-off">
          <Button variant="ghost" size="sm">
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Time Off Balances</h1>
          <p className="text-gray-500">View and adjust employee time off balances for {currentYear}</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by employee or policy..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={policyFilter} onValueChange={setPolicyFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by policy" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Policies</SelectItem>
                {policies?.map((policy) => (
                  <SelectItem key={policy.id} value={policy.id.toString()}>
                    {policy.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : Object.keys(groupedByEmployee).length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Clock className="h-12 w-12 mx-auto text-gray-400 mb-2" />
              <p>No time off balances found</p>
              <p className="text-sm mt-1">Balances will appear when policies are assigned to employees</p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(groupedByEmployee).map(([employeeId, employeeBalances]) => {
                const employee = employeeBalances[0]?.employee;
                if (!employee) return null;

                return (
                  <div key={employeeId} className="border rounded-lg overflow-hidden">
                    <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 border-b">
                      <h3 className="font-medium">
                        {employee.firstName} {employee.lastName}
                      </h3>
                      {employee.email && (
                        <p className="text-sm text-gray-500">{employee.email}</p>
                      )}
                    </div>
                    <div className="divide-y">
                      {employeeBalances.map((b) => (
                        <div
                          key={b.balance.id}
                          className="flex items-center justify-between px-4 py-3"
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: b.policy?.color || "#6B7280" }}
                            />
                            <div>
                              <p className="font-medium">{b.policy?.name}</p>
                              <p className="text-sm text-gray-500">
                                Used: {b.balance.usedBalance} {b.policy?.accrualUnit} •
                                Pending: {b.balance.pendingBalance} {b.policy?.accrualUnit}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-lg font-semibold">
                                {b.balance.currentBalance} {b.policy?.accrualUnit}
                              </p>
                              <p className="text-xs text-gray-500">Available</p>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpenAdjust(b)}
                            >
                              Adjust
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={isAdjustDialogOpen} onOpenChange={setIsAdjustDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust Balance</DialogTitle>
          </DialogHeader>
          {selectedBalance && (
            <div className="space-y-4">
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg space-y-2">
                <p>
                  <strong>Employee:</strong> {selectedBalance.employee?.firstName}{" "}
                  {selectedBalance.employee?.lastName}
                </p>
                <p>
                  <strong>Policy:</strong> {selectedBalance.policy?.name}
                </p>
                <p>
                  <strong>Current Balance:</strong> {selectedBalance.balance.currentBalance}{" "}
                  {selectedBalance.policy?.accrualUnit}
                </p>
              </div>

              <div>
                <Label>
                  Adjustment Amount <span className="text-red-500">*</span>
                </Label>
                <div className="flex gap-2 mt-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const current = parseFloat(adjustmentAmount) || 0;
                      setAdjustmentAmount((current - 1).toString());
                    }}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <Input
                    type="number"
                    step="0.5"
                    value={adjustmentAmount}
                    onChange={(e) => setAdjustmentAmount(e.target.value)}
                    placeholder="Enter amount (positive or negative)"
                    className="text-center"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const current = parseFloat(adjustmentAmount) || 0;
                      setAdjustmentAmount((current + 1).toString());
                    }}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Use positive number to add, negative to subtract
                </p>
              </div>

              <div>
                <Label>
                  Reason <span className="text-red-500">*</span>
                </Label>
                <Textarea
                  value={adjustmentReason}
                  onChange={(e) => setAdjustmentReason(e.target.value)}
                  placeholder="Reason for this adjustment..."
                />
              </div>

              {adjustmentAmount && (
                <div className="bg-blue-50 dark:bg-blue-900/30 p-3 rounded-lg">
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    New balance will be:{" "}
                    <strong>
                      {(
                        parseFloat(selectedBalance.balance.currentBalance || "0") +
                        (parseFloat(adjustmentAmount) || 0)
                      ).toFixed(2)}{" "}
                      {selectedBalance.policy?.accrualUnit}
                    </strong>
                  </p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={handleCloseAdjust}>
              Cancel
            </Button>
            <Button onClick={handleAdjust} disabled={adjustMutation.isPending}>
              {adjustMutation.isPending ? "Saving..." : "Save Adjustment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
